import { LinkIcon } from "@heroicons/react/24/outline";
import { Contact, ContactId, formatPowerOfAttorney, HomeCareLeadId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { CareProvidersHeader } from "components/clients/show-client/care-providers/care-providers-header";
import { ContactCard } from "components/contacts/card";
import {
    ContactCardList,
    ContactCardListItem,
} from "components/contacts/card/contact-card-list";
import { ContactEditor } from "components/contacts/contact-editor";
import { useInvalidateHomeCareLead } from "components/home-care-leads/use-invalidate-home-care-lead";
import { Icon } from "components/icon";
import {
    ReferenceContactPicker,
    SelectedReferenceContact,
} from "components/references/select-reference/reference-contact-picker";
import { trpc, trpcClient } from "components/rpc/trpc";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { ErrorMessage } from "components/ui/error-message";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import {
    AdvancedSearchEntityType,
    ContactWithLinksFragment,
    HomeCareLeadContactRelationship,
    useCreateContactMutation,
} from "generated/graphql";
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";

type HomeCareLeadContactItem = inferProcedureOutput<
    AppRouter["homeCareLeadContact"]["list"]
>[number];

interface HomeCareLeadContactsProps {
    homeCareLeadId: HomeCareLeadId;
    relationship: HomeCareLeadContactRelationship;
}

export const HomeCareLeadContacts: React.FC<HomeCareLeadContactsProps> = props => {
    const { homeCareLeadId, relationship } = props;

    const [editingContactId, setEditingContactId] = useState<ContactId | "_new_" | null>(
        null,
    );
    const [showReferralPicker, setShowReferralPicker] = useState(false);

    const trpcContext = trpc.useTRPC();
    const queryOptions = trpcContext.homeCareLeadContact.list.queryOptions({
        homeCareLeadId,
        relationships: [relationship],
    });
    const { data, isLoading, isError, error } = useQuery(queryOptions);
    const invalidate = useInvalidateHomeCareLead(homeCareLeadId);

    const createContactMutation = useCreateContactMutation();

    const handleAdd = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e.preventDefault();
            setEditingContactId("_new_");
        },
        [],
    );

    const handleLinkReferral = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e.preventDefault();
            setShowReferralPicker(true);
        },
        [],
    );

    const handleEdit = useCallback((contactId: ContactId) => {
        setEditingContactId(contactId);
    }, []);

    const handleDelete = useCallback(
        async (contactId: ContactId) => {
            await trpcClient.homeCareLeadContact.delete.mutate({
                homeCareLeadId,
                contactId,
                relationship,
            });
            invalidate();
        },
        [homeCareLeadId, relationship, invalidate],
    );

    const handleEditorClose = useCallback(() => {
        setEditingContactId(null);
        invalidate();
    }, [invalidate]);

    const handleEditorSaved = useCallback(
        (_contact: ContactWithLinksFragment) => {
            invalidate();
        },
        [invalidate],
    );

    const handleReferralPickerDismiss = useCallback(() => {
        setShowReferralPicker(false);
    }, []);

    const handleReferralContactSelected = useCallback(
        async (selected: SelectedReferenceContact) => {
            setShowReferralPicker(false);
            await createContactMutation.mutateAsync({
                params: {
                    linkedEntityId: homeCareLeadId,
                    linkedEntityType: AdvancedSearchEntityType.HomeCareLead,
                    linkedContactId: selected.contactId,
                    homeCareLeadContactParams: { relationship },
                },
            });
            invalidate();
        },
        [createContactMutation, homeCareLeadId, relationship, invalidate],
    );

    const isCareProvider = relationship === HomeCareLeadContactRelationship.CareProvider;
    const allowLinkReferralContact = isCareProvider;
    const panelHeaderText =
        editingContactId === "_new_"
            ? isCareProvider
                ? "Add Care Provider"
                : "Add Contact"
            : isCareProvider
              ? "Edit Care Provider"
              : "Edit Contact";

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                {isCareProvider ? (
                    <CareProvidersHeader />
                ) : (
                    <div className="flex items-center space-x-2">
                        <div className="w-7 mt-0.5">
                            <Icon name="46_MoreContacts" />
                        </div>
                        <p className="text-lg text-gray-700">Additional Contacts</p>
                    </div>
                )}
                <div className="flex items-center space-x-2">
                    <SecondaryButton onClick={handleAdd}>
                        <span className="text-sm">
                            {isCareProvider ? "Add care provider" : "Add contact"}
                        </span>
                    </SecondaryButton>
                    {allowLinkReferralContact ? (
                        <SecondaryButton onClick={handleLinkReferral}>
                            <span className="text-sm">Link referral contact</span>
                        </SecondaryButton>
                    ) : null}
                </div>
            </div>
            {isLoading ? (
                <Spinner />
            ) : isError ? (
                <ErrorMessage component="HomeCareLeadContacts.list">
                    <pre>{JSON.stringify(error, null, 2)}</pre>
                </ErrorMessage>
            ) : (
                <ContactCardList>
                    {(data || []).map(item => (
                        <HomeCareLeadContactItemCard
                            key={item.contactId}
                            item={item}
                            homeCareLeadId={homeCareLeadId}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </ContactCardList>
            )}
            <Panel
                isOpen={!!editingContactId}
                headerText={panelHeaderText}
                type={PanelType.large}
                hasCloseButton={false}>
                {editingContactId ? (
                    <ContactEditor
                        initialContactId={
                            editingContactId === "_new_" ? null : editingContactId
                        }
                        entity={{
                            type: AdvancedSearchEntityType.HomeCareLead,
                            homeCareLeadId,
                            relationship,
                        }}
                        onSaved={handleEditorSaved}
                        onClose={handleEditorClose}
                    />
                ) : null}
            </Panel>
            <Panel
                isOpen={showReferralPicker}
                headerText="Choose referral contact"
                type={PanelType.extraLarge}
                onDismiss={handleReferralPickerDismiss}>
                <ReferenceContactPicker onSelect={handleReferralContactSelected} />
            </Panel>
        </div>
    );
};

interface HomeCareLeadContactItemCardProps {
    item: HomeCareLeadContactItem;
    homeCareLeadId: HomeCareLeadId;
    onEdit: (contactId: ContactId) => void;
    onDelete: (contactId: ContactId) => void;
}

const HomeCareLeadContactItemCard: React.FC<HomeCareLeadContactItemCardProps> = props => {
    const { item, homeCareLeadId, onEdit, onDelete } = props;

    const handleEdit = useCallback(() => {
        onEdit(item.contactId);
    }, [item.contactId, onEdit]);

    const handleDelete = useCallback(() => {
        onDelete(item.contactId);
    }, [item.contactId, onDelete]);

    return (
        <ContactCardListItem>
            <ContactCard
                parentEntityType={AdvancedSearchEntityType.HomeCareLead}
                parentId={homeCareLeadId}
                contact={Contact.load(item.contact)}
                actions={
                    <>
                        {item.contact.referenceContacts.length > 0 ? (
                            <Link
                                className="block"
                                to={item.contact.referenceContacts[0].appLink}>
                                <LinkIcon className="w-4 h-4" />
                            </Link>
                        ) : (
                            <EditButton backgroundColor="bg-white" onClick={handleEdit} />
                        )}
                        <DeleteButton
                            backgroundColor="bg-white"
                            onClick={handleDelete}
                            confirm={{
                                title: `Deleting ${item.contact.name || "Contact"}`,
                                message: `Really delete the contact info for "${
                                    item.contact.name || "(No Name)"
                                }"?`,
                            }}
                        />
                    </>
                }>
                {item.powerOfAttorney?.length > 0 ? (
                    <div className="mt-2">
                        <span>Power of Attorney: </span>
                        <span className="font-medium">
                            {formatPowerOfAttorney(item.powerOfAttorney)}
                        </span>
                    </div>
                ) : null}
            </ContactCard>
        </ContactCardListItem>
    );
};
